/**
 * Customer 360 — a descriptor, not a screen.
 *
 * Everything generic comes free from the Entity 360 page: the activity timeline, the audit trail, documents,
 * approvals, exceptions and integration provenance. What is written here is only what is *particular* to a
 * customer: their doors and their standing agreements. Since 127600 (the owner, 2026-10-06) the ERP owns every customer
 * field, whichever app the record came from: status, items, days, doors and discounts are managed here (CustomerManage).
 *
 * A TAB EXISTS FOR EVERY SECTION THE BRIEF NAMES, AND EACH ONE TELLS THE TRUTH. Deliveries, payments,
 * outstanding, complaints and bottle management have no table yet, so their tabs say which epic brings them and
 * why there is nothing to show. That is deliberate: an empty "Payments" table reads as "this customer owes
 * nothing", which is a lie of a particularly expensive kind. The list comes from the loader, so the screen
 * cannot disagree with the database about what exists.
 */
import { AgreementHistoryView, AgreementPanelView } from './AgreementPanel';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, FileUp, MessageSquareWarning, Wallet, MapPin, Repeat, ShoppingCart, Tag } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { Fact, FactGrid, Panel, PanelHeader, Status, StatusBadge, Unknown, cx } from '../../ui/primitives';
import { ApprovalsSection, ProvenanceSection } from '../../entity360/sections';
import { EntityTimeline } from '../../entity360/Timeline';
import { ChangeDrawer } from './ChangeDrawer';
import { ChangeDeliveryButton, CustomerHeaderFacts, ScheduledChangeBanner, SubscriptionCalendar } from './Customer360Overview';
import { MoreMenu, ViewSwitch } from './CustomerViews';
import { ChangesHistory, MonthWise, OldKhata, ThisMonthDays } from './CustomerMonth';
import { DuesPanel, StatementPanel } from './CustomerStatement';
import { WhatsAppPanel } from './CustomerWhatsApp';
import { LinkedNumbers } from './LinkedNumbers';
import { InlineOverlays } from '../../ui/overlays';
import { CustomerBottles, CustomerDeliveries, DeliveryFacts } from './CustomerDeliveryTabs';
import { DeliveryPhotosRange } from '../delivery/DeliveryPhotos';
import { VariantBottle } from '../../ui/VariantBottle';
import { CustomerActivity, CustomerComplaints, CustomerDocuments, CustomerDoorsEdit, CustomerHealth, CustomerOutstanding, CustomerPayments } from './CustomerWorkspaceTabs';
import type { Entity360Descriptor } from '../../entity360/types';
import { istToday } from '../../lib/businessDay';
import { MissedDeliveriesCard } from './Customer360Cards';
import { CustomerProfileHead } from './CustomerProfileHead';
import { CustomerEditForm } from './CustomerEditForm';
import { DoorPlans } from './DoorPlans';
import { CustomerBoardMiddle, CustomerBoardRight } from './CustomerBoard';
import { CustomerTickets } from '../crm/ServiceDeskScreen';
import { DoorStickerCard } from '../qr/QrParts';
import { CUSTOMER_CODE_PATTERN, customerStickerPayload } from '@rtcom/domain';
import { date as fmtDate } from '../../ui/format';
import { CustomerDiscounts, CustomerStatusButton, CustomerStatusChips, MoveDoorButton, type CustomerDiscount, type CustomerPattern, type PendingChanges, type StatusDetail, type UnpaidWarning } from './CustomerManage';

export interface Customer360 {
  entity_type: string;
  id: string;
  code: string;
  name: string;
  status: 'STARTING' | 'ACTIVE' | 'PAUSED' | 'INACTIVE' | 'RETIRED';
  segment: string | null;
  billing_mode: string | null;
  price_list_code: string | null;
  phone: string | null;
  may_see_personal_data: boolean;
  city: { id: string; code: string; name: string } | null;
  route: { id: string; code: string | null } | null;
  /** Provenance only (127600: the ERP owns every customer field; source_owned_fields is always empty). */
  data_origin: string;
  source_record_id: string | null;
  source_owned_fields: string[];
  created_at: string;
  updated_at: string;
  locations: {
    id: string; code: string; label: string | null; area: string | null;
    is_primary: boolean; status: string; has_geo_pin: boolean; address_line: string | null;
  }[];
  subscriptions: {
    id: string; code: string; status: string; schedule_pattern: string;
    start_date: string; end_date: string | null; paused_from: string | null; units_per_delivery: number;
  }[] | null;
  products: {
    variant_code: string; variant_name: string; container_volume_ml: number | null; fill_volume_ml: number | null;
    assigned: boolean; takes: boolean; valid_from: string | null; last_ordered_for: string | null;
  }[];
  orders: { id: string; code: string; scheduled_date: string; status: string; source: string; location_code: string; units: number }[] | null;
  counts: { locations: number; subscriptions: number | null; orders: number | null; products: number; events: number };
  /** Each names the tab, the epic that will own it, and why it is empty. */
  not_built: { tab: string; epic: string; detail: string }[];
  // 127600: the status with its dates and reason, discounts, the days delivered, dated changes waiting, money unpaid.
  status_detail?: StatusDetail;
  discounts?: CustomerDiscount[];
  pattern?: CustomerPattern | null;
  pending_changes?: PendingChanges;
  unpaid?: UnpaidWarning | null;
}

const SUB_TONE: Record<string, 'accepted' | 'queued' | 'neutral' | 'refused' | 'info'> = {
  ACTIVE: 'accepted', DRAFT: 'info', PAUSED: 'queued', CANCELLED: 'neutral', EXPIRED: 'neutral',
};

function Doors({ entity }: { entity: Customer360 }) {
  if (entity.locations.length === 0) {
    return <div className="px-4 py-6 text-body text-ink-muted">This customer has no door on record.</div>;
  }
  return (
    <ul className="divide-y divide-line">
      {entity.locations.map((l) => (
        <li key={l.id} className="flex items-start gap-3 px-4 py-2.5">
          <MapPin size={14} className={cx('mt-0.5 shrink-0', l.is_primary ? 'text-brand' : 'text-ink-faint')} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-body">
              <span className="code-text">{l.code}</span>
              {l.label ? <span className="ml-2">{l.label}</span> : null}
              {l.is_primary ? <span className="ml-2 text-small text-brand">primary</span> : null}
              {l.status !== 'ACTIVE' ? <span className="ml-2 text-small text-ink-faint">{l.status.toLowerCase()}</span> : null}
            </p>
            <p className="text-small text-ink-muted">
              {l.address_line ?? (entity.may_see_personal_data ? <Unknown title="No street on record" /> : 'Address hidden')}
              {l.area ? ` · ${l.area}` : ''}
              {l.has_geo_pin ? ' · map pin set' : ''}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Agreements({ entity }: { entity: Customer360 }) {
  if (entity.subscriptions === null) {
    return <div className="px-4 py-6 text-body text-ink-muted">Standing agreements are shown to whoever may see subscriptions.</div>;
  }
  if (entity.subscriptions.length === 0) {
    return <div className="px-4 py-6 text-body text-ink-muted">This customer has no standing agreement.</div>;
  }
  return (
    <ul className="divide-y divide-line">
      {entity.subscriptions.map((s) => (
        <li key={s.id} className="flex items-start gap-3 px-4 py-2.5">
          <Repeat size={14} className="mt-0.5 shrink-0 text-ink-faint" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-body">
              <Link to={`/customers/subscriptions?q=${encodeURIComponent(s.code)}`} className="code-text hover:text-brand">{s.code}</Link>
              <span className="ml-2 text-small text-ink-muted">{s.schedule_pattern.toLowerCase()} · {s.units_per_delivery} unit(s) a delivery</span>
            </p>
            <p className="text-small text-ink-muted">
              from {fmtDate(s.start_date)}
              {s.end_date ? ` to ${fmtDate(s.end_date)}` : ''}
              {s.paused_from ? ` · paused from ${fmtDate(s.paused_from)}` : ''}
            </p>
          </div>
          <StatusBadge tone={SUB_TONE[s.status] ?? 'neutral'} icon={Repeat}>{s.status.toLowerCase()}</StatusBadge>
        </li>
      ))}
    </ul>
  );
}

/**
 * v7 CORE-2: a customer who is starting — made, not yet delivered to. Their first day, and what still stands in the
 * way (read from the onboarding queue, so the desk and the queue say the same thing).
 */
function StartingBanner({ customerId }: { customerId: string }) {
  const q = useQuery({ queryKey: ['onboarding-queue'], queryFn: () => rpc<{ customerId: string; firstDelivery: string | null; blockers: { code: string; text: string }[] }[]>('onboarding_queue') });
  const me = q.data?.find((x) => x.customerId === customerId);
  if (!me) return null;
  return (
    <div role="status" className={cx('mb-3 rounded-md border px-4 py-2.5 text-body', me.blockers.length ? 'border-queued/40 bg-queued/5' : 'border-info/40 bg-info/5')}>
      <span className="font-medium">Starting.</span>{' '}
      {me.blockers.length === 0
        ? <>First delivery {me.firstDelivery ? fmtDate(me.firstDelivery) : 'on the plan\'s start date'}; becomes active that day.</>
        : <>Not delivered to yet: {me.blockers.map((b) => b.text).join('; ')}. <Link className="underline" to="/crm/onboarding">Onboarding</Link></>}
    </div>
  );
}

function Commercial({ entity }: { entity: Customer360 }) {
  return (
    <div>
      <FactGrid columns={2}>
        <Fact label="Segment">{entity.segment ?? <Unknown title="The ERP's own classification; nobody has set one" />}</Fact>
        <Fact label="Billing mode">
          {entity.billing_mode?.toLowerCase() ?? <Unknown title="Which billing modes exist is the owner’s decision; until it is answered a customer carries none" />}
        </Fact>
        <Fact label="Price list" mono>
          {entity.price_list_code ?? <Unknown title="No price master exists; whether the ERP owns one is the owner’s decision" />}
        </Fact>
        <Fact label="Round" mono>{entity.route?.code ?? <Unknown title="Not on a round" />}</Fact>
        <Fact label="City">{entity.city?.name ?? <Unknown />}</Fact>
        {/* v7 (M12-D3): the phone is on the Contact card, masked; the full number only through the logged reveal. */}
      </FactGrid>
    </div>
  );
}


/**
 * What this customer is SET UP for, beside what they actually TAKE.
 *
 * A variant taken without an assignment is not marked as an error here. Whether an assignment should restrict
 * what a customer may order is a commercial policy nobody has stated (CD-67), so the screen shows the fact and
 * leaves the judgement to whoever owns the policy.
 */
function Products({ entity }: { entity: Customer360 }) {
  if (entity.products.length === 0) {
    return <div className="px-4 py-6 text-body text-ink-muted">This customer is set up for nothing and takes nothing.</div>;
  }
  return (
    <div>
      <table className="w-full text-body">
        <thead>
          <tr className="border-b border-line text-left">
            <th className="px-4 py-2 label-text">Variant</th>
            <th className="px-4 py-2 label-text">Container</th>
            <th className="px-4 py-2 label-text">Filled to</th>
            <th className="px-4 py-2 label-text">Set up</th>
            <th className="px-4 py-2 label-text">Takes</th>
            <th className="px-4 py-2 label-text">Last ordered for</th>
          </tr>
        </thead>
        <tbody>
          {entity.products.map((p) => (
            <tr key={p.variant_code} className="border-b border-line last:border-b-0">
              <td className="px-4 py-2"><VariantBottle code={p.variant_code} size={26} /> <span className="text-ink-muted">{p.variant_name}</span></td>
              {/* The container size and the milk fill are separate facts and stay separate columns. */}
              <td className="px-4 py-2 tabular text-ink-muted">{p.container_volume_ml ? `${p.container_volume_ml} ml` : <Unknown />}</td>
              <td className="px-4 py-2 tabular text-ink-muted">{p.fill_volume_ml ? `${p.fill_volume_ml} ml` : <Unknown />}</td>
              <td className="px-4 py-2">{p.assigned ? <span className="text-accepted">yes{p.valid_from ? `, since ${fmtDate(p.valid_from)}` : ''}</span> : <span className="text-ink-faint">no</span>}</td>
              <td className="px-4 py-2">{p.takes ? 'yes' : <span className="text-ink-faint">no</span>}</td>
              <td className="px-4 py-2 text-ink-muted">{p.last_ordered_for ? fmtDate(p.last_ordered_for) : <Unknown title="Never ordered" />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {entity.products.some((p) => p.takes && !p.assigned) ? (
        <p className="border-t border-line px-4 py-2 text-small text-ink-muted">
          This customer takes a variant they are not set up for. Whether that should be prevented is the owner’s decision;
          the ERP records it rather than deciding.
        </p>
      ) : null}
    </div>
  );
}

function Orders({ entity }: { entity: Customer360 }) {
  if (entity.orders === null) {
    return <div className="px-4 py-6 text-body text-ink-muted">Orders are shown to whoever may see them.</div>;
  }
  if (entity.orders.length === 0) {
    return <div className="px-4 py-6 text-body text-ink-muted">No order has been placed for this customer.</div>;
  }
  return (
    <ul className="divide-y divide-line">
      {entity.orders.map((o) => (
        <li key={o.id} className="flex items-start gap-3 px-4 py-2.5">
          <ShoppingCart size={14} className="mt-0.5 shrink-0 text-ink-faint" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-body">
              <Link to={`/customers/orders/${o.id}`} className="code-text hover:text-brand">{o.code}</Link>
              <span className="ml-2 text-small text-ink-muted">for {fmtDate(o.scheduled_date)} · {o.units} unit(s) · {o.location_code}</span>
            </p>
          </div>
          <StatusBadge tone={o.status === 'CONFIRMED' ? 'accepted' : o.status === 'PLANNED' ? 'info' : 'neutral'} icon={ShoppingCart}>{o.status.toLowerCase()}</StatusBadge>
        </li>
      ))}
    </ul>
  );
}

function QuickButton({ icon: Icon, label, onClick }: { icon: typeof CalendarDays; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="btn btn-secondary inline-flex h-[30px] items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-3 text-small font-medium text-ink hover:bg-surface-sunken">
      <Icon size={14} aria-hidden />{label}
    </button>
  );
}

/** The customer's health in a word, a shape and a colour (the owner's "at risk": complaints, a pause, > 7 unpaid days). */
function CustomerHealthChip({ customerId }: { customerId: string }) {
  const q = useQuery({ queryKey: ['customer-health', customerId], queryFn: () => rpc<{ level: 'GREEN' | 'AMBER' | 'RED'; reasons: { text: string }[] }>('customer_health', { p_customer_id: customerId }) });
  if (!q.data) return null;
  const { level, reasons } = q.data;
  return (
    <span title={reasons.map((r) => r.text).join(' · ') || 'No complaint, no pause, paid within 7 delivery days.'}
      className={cx('inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-small font-medium',
        level === 'GREEN' ? 'border-accepted text-accepted bg-accepted-ground' : level === 'AMBER' ? 'border-queued text-queued bg-queued-ground' : 'border-refused text-refused bg-refused-ground')}>
      {level === 'GREEN' ? '● Healthy' : level === 'AMBER' ? '▲ Watch' : '■ At risk'}
    </span>
  );
}

/**
 * Overview › Doors (the owner, 2026-10-22): every address with Add / move address, the gate sticker, and each door's own
 * edit (gate photo, landmark chip). The words and pins are also in Personal & family's form.
 */
function DoorsView({ entity: e }: { entity: Customer360 }) {
  const door = e.locations.find((l) => l.is_primary) ?? e.locations[0];
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <div className="xl:col-span-2"><DoorPlans entity={e} /></div>
      <Panel className="overflow-hidden"><PanelHeader title="Addresses & sticker" actions={<MoveDoorButton entity={e} />} /><Doors entity={e} />
        <DoorStickerCard doorId={door?.id ?? ''} sticker={CUSTOMER_CODE_PATTERN.test(e.code) && e.locations.length ? customerStickerPayload(e.code) : null} /></Panel>
      <Panel className="overflow-hidden"><CustomerDoorsEdit customerId={e.id} /></Panel>
    </div>
  );
}

/** The older tab and view ids (D16, D19 phase 21) and the v7 tab that holds each one now. A shared link keeps working. */
export const CUSTOMER_TAB_ALIASES: Readonly<Record<string, string>> = {
  family: 'overview', profile: 'overview', locations: 'overview', source: 'overview',
  subscription: 'calendar', subscriptions: 'calendar', products: 'calendar', 'dated-changes': 'calendar',
  orders: 'deliveries', photos: 'deliveries', bottles: 'deliveries', 'bottle-management': 'deliveries',
  bills: 'money', payments: 'money', outstanding: 'money', invoices: 'money', khata: 'money', 'old-khata': 'money', dues: 'money', statement: 'money',
  months: 'calendar', 'this-month': 'calendar',
  tickets: 'service', complaints: 'service',
  history: 'activity', documents: 'activity', approvals: 'activity', audit: 'activity',
};

const photosFrom = () => new Date(Date.now() - 85 * 86_400_000).toISOString().slice(0, 10);
const photosTo = () => istToday();

export const customerDescriptor: Entity360Descriptor<Customer360> = {
  entityType: 'sales.customers',
  kind: 'customers',
  registerPath: '/customers',
  registerLabel: 'Customers',
  tabAliases: CUSTOMER_TAB_ALIASES,

  load: (id) => rpc<Customer360>('customer_360', { p_id: id }),
  // The board's actions (D19 phase 21b): "Change delivery" first, then what the desk does most; the rest under More.
  // Each of the D19 phase 10 buttons still opens the tab (and view) that does it.
  quickActions: (e, open) => (
    <>
      <ChangeDeliveryButton />
      <CustomerStatusButton entity={e} />
      <QuickButton icon={Wallet} label="Record a payment" onClick={() => open('money')} />
      <QuickButton icon={MessageSquareWarning} label="Log a complaint" onClick={() => open('service')} />
      <MoreMenu items={[
        <QuickButton key="day" icon={CalendarDays} label="Change a day" onClick={() => open('calendar')} />,
        <QuickButton key="doc" icon={FileUp} label="Add a document" onClick={() => open('activity')} />,
      ]} />
      <CustomerHealthChip customerId={e.id} />
    </>
  ),
  // Board 04 (the owner's CRM brief, 2026-10-18): the profile band replaces the title, status, facts and actions below.
  // The owner, 2026-10-22: one band, ✎ opens the edit section below it, eight KPIs above the tabs.
  head: (e, open) => <CustomerProfileHead entity={e} open={open} />,
  title: (e) => e.name,
  code: (e) => e.code,

  // Customer type, record status and the data check (the board's three pills).
  status: (e) => {
    const door = e.locations.find((l) => l.is_primary) ?? e.locations[0];
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {e.data_origin !== 'ERP' ? <Status code="SIMULATION" /> : null}
        {e.segment ? <StatusBadge tone="info" icon={Tag}>{e.segment}</StatusBadge> : null}
        <CustomerStatusChips entity={e} />
        {door ? (door.has_geo_pin
          ? <StatusBadge tone="neutral" icon={MapPin}>Pin set</StatusBadge>
          : <StatusBadge tone="queued" icon={MapPin}>No map pin</StatusBadge>)
          : <StatusBadge tone="queued" icon={MapPin}>No door</StatusBadge>}
      </div>
    );
  },

  summary: (e) => <CustomerHeaderFacts entity={e} />,
  banner: (e) => <>{e.status === 'STARTING' ? <StartingBanner customerId={e.id} /> : null}<ScheduledChangeBanner entity={e} /></>,
  overlay: (e) => <ChangeDrawer entity={e} />,

  // The owner, 2026-10-22: no left side on any tab (contact, doors and the profile are under ✎ in the head); the board's
  // right column (units per month, account, bottles, recent) is the Overview's second column.

  // The owner, 2026-10-07: tabs AND sub-tabs (this replaces v7 Part D §5's "no sub-tabs"); a sub-tab is a view in the URL
  // (?view=), so an old link still lands on its content. Inside a tab every form opens in place (InlineOverlays).
  tabs: [
    { id: 'overview', label: 'Overview', section: 'custom', bare: true, render: (entity) => (
      <InlineOverlays><ViewSwitch bare label="Overview" views={[
        // Board 04: the day's delivery, the month, the standing plan.
        { id: 'overview', label: 'At a glance', render: () => (
          <div className="grid gap-4 xl:grid-cols-2">
            <div className="grid min-w-0 content-start gap-4"><CustomerBoardMiddle entity={entity} /></div>
            <div className="grid min-w-0 content-start gap-4"><MissedDeliveriesCard customerId={entity.id} /><CustomerBoardRight entity={entity} /></div>
          </div>) },
        // The owner, 2026-10-22: the onboarding form, filled, to edit; the head's Edit opens this view.
        { id: 'family', label: 'Personal & family', render: () => (
          <div className="grid gap-4">
            <CustomerEditForm entity={entity} />
            <Panel className="overflow-hidden"><PanelHeader title="Terms" /><Commercial entity={entity} /></Panel>
          </div>) },
        { id: 'locations', label: 'Doors', render: () => <DoorsView entity={entity} /> },
      ]} /></InlineOverlays>) },
    { id: 'calendar', label: 'Subscription', section: 'custom', bare: true, permission: 'subscription.view', render: (entity) => (
      <InlineOverlays><ViewSwitch bare label="Subscription" views={[
        { id: 'agreement', label: 'Agreement', render: () => <AgreementPanelView entity={entity} /> },
        { id: 'calendar', label: 'This month', render: () => (
          <div className="grid gap-4">
            <SubscriptionCalendar entity={entity} />
            <ThisMonthDays customerId={entity.id} />
          </div>) },
        { id: 'months', label: 'Month-wise', render: () => <MonthWise customerId={entity.id} /> },
        { id: 'subscriptions', label: 'Plan & items', render: () => (
          <div className="grid gap-4">
            <Panel className="overflow-hidden"><PanelHeader title="Plans" /><Agreements entity={entity} /></Panel>
            <Panel className="overflow-hidden"><PanelHeader title="Items" /><Products entity={entity} /></Panel>
          </div>) },
        { id: 'dated-changes', label: 'History', render: () => <AgreementHistoryView entity={entity} /> },
      ]} /></InlineOverlays>) },
    { id: 'deliveries', label: 'Deliveries & bottles', section: 'custom', bare: true, permissionAnyOf: ['order.view', 'delivery.view', 'bottles.view'], render: (entity) => (
      <InlineOverlays><ViewSwitch bare label="Deliveries and bottles" views={[
        { id: 'deliveries', label: 'Deliveries', permission: 'delivery.view', render: () => <div className="grid gap-4"><Panel className="overflow-hidden"><DeliveryFacts customerId={entity.id} /></Panel><Panel className="overflow-hidden"><PanelHeader title="Deliveries" /><CustomerDeliveries customerId={entity.id} /></Panel></div> },
        { id: 'bottles', label: 'Bottles', permission: 'bottles.view', render: () => <Panel className="overflow-hidden"><PanelHeader title="Bottles" /><CustomerBottles customerId={entity.id} /></Panel> },
        { id: 'photos', label: 'Photos', permission: 'delivery.view',
          render: () => <DeliveryPhotosRange from={photosFrom()} to={photosTo()} customerCode={entity.code} title="Photos at this door, last 12 weeks" /> },
        { id: 'orders', label: 'Orders', permission: 'order.view', render: () => <Panel className="overflow-hidden"><PanelHeader title="Orders" /><Orders entity={entity} /></Panel> },
      ]} /></InlineOverlays>) },
    // Money (migrations 207–210, D16 Phases 2–4; the old khata, 128400). Nothing on them deletes.
    { id: 'money', label: 'Money', section: 'custom', bare: true, permission: 'customer.view', render: (entity) => (
      <InlineOverlays><ViewSwitch bare label="Money" views={[
        { id: 'dues', label: 'What is owed', permission: 'customer.payment.view', render: () => <DuesPanel customerId={entity.id} /> },
        { id: 'outstanding', label: 'Bill & ledger', render: () => <CustomerOutstanding customerId={entity.id} /> },
        { id: 'statement', label: 'Print bill', permission: 'customer.payment.view', render: () => <StatementPanel entity={entity} /> },
        { id: 'payments', label: 'Payments', render: () => <Panel className="overflow-hidden"><PanelHeader title="Payments" /><CustomerPayments customerId={entity.id} /></Panel> },
        { id: 'khata', label: 'Old khata', permission: 'customer.payment.view', render: () => <OldKhata customerId={entity.id} /> },
        { id: 'discounts', label: 'Discounts', render: () => <CustomerDiscounts entity={entity} /> },
      ]} /></InlineOverlays>) },
    // WhatsApp (the owner, 2026-10-14): the bill, a reminder, a thank-you or an own message to the first or second number.
    { id: 'whatsapp', label: 'WhatsApp', section: 'custom', bare: true, permission: 'customer.view', render: (entity) => <><WhatsAppPanel entity={entity} /><LinkedNumbers customerId={entity.id} /></> },
    { id: 'service', label: 'Service', section: 'custom', bare: true, permission: 'customer.view', render: (entity) => (
      <InlineOverlays><ViewSwitch bare label="Service" views={[
        { id: 'tickets', label: 'Tickets', render: () => <div className="grid gap-4"><Panel className="overflow-hidden"><CustomerHealth customerId={entity.id} /></Panel><CustomerTickets customerId={entity.id} /></div> },
        { id: 'complaints', label: 'Complaints', render: () => <Panel className="overflow-hidden"><PanelHeader title="Complaints" /><CustomerComplaints customerId={entity.id} /></Panel> },
      ]} /></InlineOverlays>) },
    // ID proof, deposit receipts, agreements (migrations 206, 209): folders by type, previews, archive with a reason, never deleted.
    { id: 'activity', label: 'Activity', section: 'custom', bare: true, render: (entity, context) => (
      <InlineOverlays><ViewSwitch bare label="Activity" views={[
        { id: 'history', label: 'Timeline', render: () => (
          <div className="grid gap-4">
            <Panel className="overflow-hidden"><PanelHeader title="Activity" /><div className="p-4"><CustomerActivity customerId={entity.id} /></div></Panel>
            <Panel className="overflow-hidden"><PanelHeader title="Record history" /><EntityTimeline {...context} kinds={['BUSINESS', 'INTEGRATION', 'APPROVAL', 'EXCEPTION']} emptyLabel="No activity yet" /></Panel>
            <Panel className="overflow-hidden"><ProvenanceSection {...context} /></Panel>
          </div>) },
        { id: 'documents', label: 'Documents', permission: 'customer.pii.view', render: () => <CustomerDocuments customerId={entity.id} /> },
        { id: 'approvals', label: 'Approvals', render: () => <Panel className="overflow-hidden"><PanelHeader title="Approvals" /><ApprovalsSection {...context} /></Panel> },
        { id: 'audit', label: 'Audit', permission: 'audit.view', render: () => <Panel className="overflow-hidden"><PanelHeader title="Audit" /><EntityTimeline {...context} kinds={['CHANGE']} emptyLabel="No changes recorded" /></Panel> },
      ]} /></InlineOverlays>) },
  ],
};
